import { v4 as uuid } from 'uuid';
import {
  ALIGNMENT_MAP,
  NEXT_SEAT_SIDES,
  OPPOSITE_SEAT_SIDES,
  PREVIOUS_SEAT_SIDES,
  PRODUCT_KEYS,
  SIDE_KEYWORD,
} from '../constants';
import {
  getAlignKeyword,
  getNextValidLocalSide,
  depthFirstTraverse,
  swapSideKey,
} from '../utils';
import { convertSideEvent } from '../events';

import Seat from './Seat';
import Side from './Side';
import RollarmSide from './RollarmSide';
import WedgeSeat from './WedgeSeat';
import DeepSide from './DeepSide';
import DoubleSidedSide from './DoubleSidedSide';
import StandardOutdoorSeat from './StandardOutdoorSeat';
import StandardOutdoorSide from './StandardOutdoorSite';
import DeepOutdoorSide from './DeepOutdoorSite';
import AngledSide from './AngledSide';
import DeepAngledSide from './DeepAngledSide';
import Anytable from './Anytable';
import SweptArm from './SweptArm';
import Connector from './Connector';

export const ItemMap = {
  seat: {
    standard: Seat,
    wedge: WedgeSeat,
    standard_outdoor: StandardOutdoorSeat,
  },
  side: {
    standard: Side,
    deep: DeepSide,
    doubleSided: DoubleSidedSide,
    rollArm: RollarmSide,
    standard_outdoor: StandardOutdoorSide,
    deep_outdoor: DeepOutdoorSide,
    angled: AngledSide,
    deepAngled: DeepAngledSide,
    sweptArm: SweptArm,
  },
  anytable: {
    standard: Anytable,
    '2shelves': Anytable,
    shelfDrawer: Anytable,
  },
};

export default class Island {
  constructor(threekitApi, items, options = {}) {
    this._id = options.id || uuid();
    this._threekitApi = threekitApi;
    this._firstItem = null;
    this._lastItem = null;
    this._items = [];

    this.layout = null;

    this._setItems(items);
  }

  // find the seat item that local at top/left in the world space
  _setFirstItem = () => {
    const seatItems = this.getSeatItems();

    if (!seatItems.length) return null;

    let firstItem = seatItems[0];
    let firstTLTrans = firstItem.getVertexWorldTranslation('topLeft');

    // first find the item which its topLeft vertex has the smallest z and x val
    for (let idx = 1; idx < seatItems.length; ++idx) {
      const curItem = seatItems[idx];
      const curTLTrans = curItem.getVertexWorldTranslation('topLeft');

      if (
        curTLTrans.z < firstTLTrans.z ||
        (curTLTrans.z === firstTLTrans.z && curTLTrans.x < firstTLTrans.x)
      ) {
        firstItem = curItem;
        firstTLTrans = curTLTrans;
      }
    }
    // the logic we use will check z coordinate first, but there are case that
    // an item has smallest z coordinate, but it still has item coonect to its left
    // second check if there are still seat connect to its left
    let leftItem = firstItem.getConnectedItemWithWorldSide('left');
    while (leftItem && leftItem._type === 'seat') {
      firstItem = leftItem;
      leftItem = firstItem.getConnectedItemWithWorldSide('left');
    }

    this._firstItem = firstItem;
  };

  _setLastItem = () => {
    let lastItem = this._firstItem;

    let rightItem = lastItem?.getConnectedItemWithWorldSide('right');
    while (rightItem) {
      if (rightItem._type === 'seat') lastItem = rightItem;

      rightItem = rightItem.getConnectedItemWithWorldSide('right');
    }

    this._lastItem = lastItem;
  };

  _setItems = (items) => {
    this._items = items || [];
    this._setFirstItem();
    this._setLastItem();

    this._items.forEach((item) => {
      item.island = this._id;
    });
  };

  _getItemsByType = (type, key) =>
    this._items.filter(
      (item) => item._type === type && (key ? item._key === key : true)
    );

  // this will connect the specific item to the connector, and update all item connect to this specific item as well
  _connectRecersive = (root, connector) => {
    const visited = new Set();

    const connectHelper = (currentRoot, currentConnector) => {
      if (visited.has(currentRoot)) return;
      visited.add(currentRoot);

      currentRoot._connectTo(
        currentConnector instanceof Connector
          ? currentConnector
          : new Connector(currentConnector)
      );

      const rootConnectLocalSide = SIDE_KEYWORD.find(
        (side) => currentRoot[side] === currentConnector.target
      );

      SIDE_KEYWORD.forEach((side) => {
        if (side === rootConnectLocalSide || !currentRoot[side]) return;

        connectHelper(
          currentRoot[side],
          new Connector({
            target: currentRoot,
            targetLocalSide: side,
            alignment: currentRoot[getAlignKeyword(side)],
            localSide: SIDE_KEYWORD.find(
              (s) => currentRoot[side][s] === currentRoot
            ),
          })
        );
      });
    };
    connectHelper(
      root,
      connector instanceof Connector ? connector : new Connector(connector)
    );
  };

  // TODO receive a layout and check deemed connections
  /**
   * Given an item return all the valid connector where it can go
   * @param {*} item
   * @param {*} options
   * @returns
   */
  getConnector = (item, options = {}) => {
    const validationItems = options.validationItems || this._items;
    const insertAtEnd = !!options.insertAtEnd; // render plus sign with insert mode at left of the firstItem and right of lastItem even if that side already has a side connected
    const autoRotate = !!options.autoRotate; // automatically apply the rotation based on target item

    const sideItems = this.getSideItems();
    const seatItems = this.getSeatItems();

    let connectorItems = seatItems.concat(this._getItemsByType('anytable'));
    const { rotation: initRotation } = item;
    const isAnytable = item._type === 'anytable';

    if (item._type === 'seat') {
      connectorItems = connectorItems.concat(
        this._getItemsByType('side', 'doubleSided')
      );
    }

    // when handling insert of X between A and B, we just need one connector from either of them
    const connections = {};

    const connectorsByItemBySide = connectorItems.reduce((acc, targetItem) => {
      targetItem.connectors = {};
      if (!item.validateConnection(targetItem)) return acc;

      if (
        item._type === 'side' &&
        targetItem._type === 'anytable' &&
        !sideItems.length
      ) {
        return acc;
      }

      acc[targetItem._id] = {};

      SIDE_KEYWORD.forEach((localSide) => {
        if (!targetItem.hasOwnProperty(localSide)) return;

        const { rotation: targetRotation } = targetItem;
        if (autoRotate) item.setRotation(targetRotation);

        const worldSide = targetItem.getWorldSide(localSide);
        if (targetItem[localSide] !== null) {
          if (item.validateConnection(targetItem[localSide])) {
            if (
              insertAtEnd &&
              item._type === 'seat' &&
              targetItem[localSide]._type === 'side' &&
              targetItem[localSide]._key !== 'doubleSided' &&
              ((targetItem === this._firstItem && worldSide === 'left') ||
                (targetItem === this._lastItem && worldSide === 'right'))
            ) {
              acc[targetItem._id][localSide] = [
                new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: worldSide === 'left' ? 'bottom' : 'top',
                  insert: true,
                }),
              ];
            } else if (isAnytable) {
              if (
                targetItem._type === 'seat' &&
                targetItem[localSide]._type === 'seat' &&
                !(
                  connections[targetItem._id] &&
                  connections[targetItem._id] === targetItem[localSide]._id
                )
              ) {
                // insert between seats
                const connector = new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: 'bottom',
                  insert: true,
                  anytable: true,
                });

                connections[targetItem[localSide]._id] = targetItem._id;
                acc[targetItem._id][localSide] = [connector];
              } else if (
                targetItem._type === 'anytable' &&
                targetItem[localSide]._type === 'seat'
              ) {
                // insert between anytable & seat
                acc[targetItem._id][localSide] = [
                  new Connector({
                    target: targetItem,
                    targetLocalSide: localSide,
                    alignment: 'bottom',
                    insert: true,
                    anytable: true,
                  }),
                ];
              } else if (
                targetItem._type === 'seat' &&
                targetItem[localSide]._type === 'side' &&
                targetItem[localSide].style === 'arm'
              ) {
                // handle append arm side
                const sideConnectionSide = Object.keys(ALIGNMENT_MAP).find(
                  (s) => targetItem[localSide][s]?._id === targetItem._id
                );
                const oppositeSide = OPPOSITE_SEAT_SIDES[sideConnectionSide];
                if (!targetItem[localSide][oppositeSide]) {
                  acc[targetItem._id][localSide] = [
                    new Connector({
                      target: targetItem[localSide],
                      targetLocalSide: oppositeSide,
                      alignment: 'bottom',
                      anytable: true,
                    }),
                  ];
                }
              }
            }
          }
          return;
        }

        const targetSideDimension = targetItem.getLocalSideDimension(localSide);
        const itemWorldSide = OPPOSITE_SEAT_SIDES[worldSide];
        const itemLocalSide =
          item._type === 'side' ? 'bottom' : item.getLocalSide(itemWorldSide);
        const itemDimension =
          item._type === 'side'
            ? item.width
            : item.getLocalSideDimension(itemLocalSide);

        if (
          item._key.indexOf('deep') !== -1 &&
          targetSideDimension !== itemDimension
        ) {
          console.log(
            'deep side can not attach to the side longer than its width',
            targetItem,
            targetSideDimension,
            itemDimension
          );
          return;
        }

        if (
          item._key.indexOf('deep') === -1 &&
          targetItem.top?._type === 'seat' &&
          targetItem.bottom?._type === 'seat' &&
          /left|right/.test(localSide)
        ) {
          return;
        }

        if (item._type === 'side' && item._key === PRODUCT_KEYS.SWEPTARM) {
          const targetItemOppositeItem =
            targetItem[OPPOSITE_SEAT_SIDES[localSide]];
          const targetItemNextItem = targetItem[NEXT_SEAT_SIDES[localSide]];
          const targetItemPreviousItem =
            targetItem[PREVIOUS_SEAT_SIDES[localSide]];
          if (targetItemOppositeItem?._type === 'seat') {
            let alignment = 'top';
            if (targetItemPreviousItem) {
              if (targetItemPreviousItem._type === 'side') {
                if (targetItemNextItem && targetItemNextItem._type === 'side') {
                  // if the previous item is a side, and the next item is a side, it's a 1 seat layout
                  // we can not attach the swept arm to this side as a back side
                  return;
                }
                alignment = 'bottom';
              }
            }
            acc[targetItem._id][localSide] = [
              new Connector({
                target: targetItem,
                targetLocalSide: localSide,
                alignment,
              }),
            ];
          } else if (targetItemOppositeItem?._type === 'side') {
            // handle 1 seat
            if (
              targetItemNextItem?._type === 'side' &&
              (!targetItemPreviousItem ||
                targetItemPreviousItem._type !== 'side')
            ) {
              acc[targetItem._id][localSide] = [
                new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: localSide === 'right' ? 'bottom' : 'top',
                }),
              ];
            } else if (
              targetItemPreviousItem?._type === 'side' &&
              (!targetItemNextItem || targetItemNextItem._type !== 'side')
            ) {
              acc[targetItem._id][localSide] = [
                new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: localSide === 'right' ? 'bottom' : 'top',
                }),
              ];
            }
            return;
          } else if (!targetItemOppositeItem) {
            // handle 1 seat
            if (!targetItemNextItem && !targetItemPreviousItem) {
              acc[targetItem._id][localSide] = [
                new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: localSide === 'right' ? 'bottom' : 'top',
                }),
              ];
            } else if (
              targetItemPreviousItem?._type === 'side' &&
              (!targetItemNextItem || targetItemNextItem._type !== 'side')
            ) {
              acc[targetItem._id][localSide] = [
                new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: localSide === 'right' ? 'bottom' : 'top',
                }),
              ];
            } else if (
              targetItemNextItem?._type === 'side' &&
              (!targetItemPreviousItem ||
                targetItemPreviousItem._type !== 'side')
            ) {
              acc[targetItem._id][localSide] = [
                new Connector({
                  target: targetItem,
                  targetLocalSide: localSide,
                  alignment: localSide === 'right' ? 'bottom' : 'top',
                }),
              ];
            }
          }
          return;
        }

        const res = [];

        let defaultAlignment;

        switch (worldSide) {
          case 'left':
            defaultAlignment = item._type === 'side' ? 'top' : 'bottom';
            break;
          case 'top':
            defaultAlignment =
              targetItem === this._firstItem ? 'bottom' : 'top';
            break;
          case 'right':
            defaultAlignment = item._type === 'side' ? 'bottom' : 'top';
            break;
          default:
            defaultAlignment =
              targetItem === this._firstItem || item._type === 'side'
                ? 'bottom'
                : 'top';
            break;
        }

        if (isAnytable) {
          if (/top|bottom/.test(localSide)) {
            res.push(
              new Connector({
                target: targetItem,
                targetLocalSide: localSide,
                alignment: defaultAlignment,
                localSide: itemLocalSide,
              })
            );
          } else if (
            targetItem[NEXT_SEAT_SIDES[localSide]] &&
            !targetItem[PREVIOUS_SEAT_SIDES[localSide]]
          ) {
            res.push(
              new Connector({
                target: targetItem,
                targetLocalSide: localSide,
                alignment: 'top',
                localSide: 'bottom',
              })
            );
          } else {
            res.push(
              new Connector({
                target: targetItem,
                targetLocalSide: localSide,
                alignment:
                  targetItem[NEXT_SEAT_SIDES[localSide]] &&
                  targetItem[NEXT_SEAT_SIDES[localSide]]._type === 'side'
                    ? 'top'
                    : 'bottom',
                localSide: 'bottom',
              })
            );
          }
        } else {
          const connector = new Connector({
            target: targetItem,
            targetLocalSide: localSide,
            alignment: defaultAlignment,
            localSide: itemLocalSide,
          });
          if (
            item._type === 'side' &&
            targetItem._key === 'wedge' &&
            localSide === 'left'
          ) {
            connector.alignment = 'top';
          }

          res.push(connector);

          if (itemDimension !== targetSideDimension) {
            res.push(
              new Connector({
                target: targetItem,
                targetLocalSide: localSide,
                alignment: ALIGNMENT_MAP[defaultAlignment],
                localSide: itemLocalSide,
              })
            );
          }
        }

        acc[targetItem._id][localSide] = res.filter((connector) => {
          try {
            if (connector.insert) return true;
            item._connectTo(connector);
            const valid = validationItems
              .filter((validationItem) => validationItem !== targetItem)
              .every((existItem) => !existItem.intersectWith(item));
            item._disconnect();
            item.setRotation(autoRotate ? targetRotation : initRotation);
            return valid;
          } catch (e) {
            item._disconnect();
            item.setRotation(autoRotate ? targetRotation : initRotation);
            return false;
          }
        });
      });
      SIDE_KEYWORD.forEach((side) => {
        if (acc[targetItem._id][side] && acc[targetItem._id][side].length) {
          targetItem.connectors[side] = acc[targetItem._id][side];
        }
      });

      return acc;
    }, {});
    const allConnectors = Object.entries(connectorsByItemBySide)
      .map(([itemId, val]) => {
        const results = [];
        SIDE_KEYWORD.forEach((side) => {
          if (val[side] && val[side].length) {
            results.push(val[side]);
          }
        });
        return results;
      })
      .filter((val) => val.length);

    item.setRotation(initRotation);
    return allConnectors;
  };

  // the addItem will make update the firstItem if needed (new item connect to the world left of the current first item)
  addItem = async (item, connector) => {
    this.pushItem(item, connector);
  };

  // push existing item to the island, this will not init the item
  pushItem = (item, connector) => {
    this._items.push(item);
    item._connectTo(connector);
    if (item._type === 'seat' && connector.target === this._firstItem) {
      this._setFirstItem();
    }

    this._setLastItem();
  };

  // difference behavious will be applyed, based on the remaining layout, when delete an item
  // after disconnect the item, it will start traverse from all its connected items to to find the union between the remaining items
  // 1. If item has no seat connection, it will delete the item and all connect items
  // 2. if the item only has one seat connection, it will delete the item, connect any item on the opposite side (left <-> right, top <-> bottom) to the item on seat connection side, and delete all items on the vertical side
  // 3. if the item has two seat connections
  //   a. If after deletion, the remaining item are still connected (means a circle exist before), we simply delete the item and all vertical side connection.
  //   b. If two seat connections are along the opposite side, we delete the item and its vertical direction connected items, and re-connect the two items along horizontal size
  //   c. If two seat connections are in vertical direction. We delete the item and all non-seat connection, and split the island into two
  // 4. If the item has more than two seat connections, we will just delete the item itself(along with all non-seat connection), and split and update island when needed

  deleteItem = (itemToDelete, update = true) => {
    if (itemToDelete.island !== this._id) return;

    let deletedNodes = [itemToDelete];
    const isolateSets = [];
    let snapTranslation = null;

    const existConnections = itemToDelete.getConnections();
    const existSeatConnections = existConnections.filter(
      (connection) =>
        connection.target._type === 'seat' ||
        connection.target._type === 'anytable'
    );

    const connections = [];

    if (existSeatConnections.length === 0 && itemToDelete._type === 'seat') {
      deletedNodes = deletedNodes.concat(
        existConnections.map(({ target }) => target)
      );
    } else if (
      existSeatConnections.length === 1 ||
      (existSeatConnections.length === 2 &&
        existSeatConnections[0].localSide ===
          OPPOSITE_SEAT_SIDES[existSeatConnections[1].localSide])
    ) {
      const thisSide = existSeatConnections[0].localSide;
      const oppositeSide = OPPOSITE_SEAT_SIDES[thisSide];
      const oppositeItem = itemToDelete[oppositeSide];

      SIDE_KEYWORD.forEach((side) => {
        if (side !== thisSide && side !== oppositeSide && itemToDelete[side]) {
          deletedNodes.push(itemToDelete[side]);
        }
      });
      // connect the opposite item
      if (oppositeItem) {
        const initTranslation = oppositeItem.translation;
        const target = itemToDelete[thisSide];
        const targetLocalSide = SIDE_KEYWORD.find(
          (side) => itemToDelete[thisSide][side] === itemToDelete
        );
        const oppositeItemConnectSide = SIDE_KEYWORD.find(
          (side) => oppositeItem[side] === itemToDelete
        );
        const newConnector = new Connector({
          target,
          targetLocalSide,
          alignment: target[getAlignKeyword(targetLocalSide)],
          localSide: oppositeItemConnectSide,
        });

        if (oppositeItem._type === 'side') {
          newConnector.alignment = itemToDelete[getAlignKeyword(oppositeSide)];
        } else if (
          update &&
          itemToDelete._type === 'side' &&
          target._type === 'anytable' &&
          oppositeItem._type === 'seat' &&
          (oppositeItemConnectSide === 'top' ||
            oppositeItemConnectSide === 'bottom')
        ) {
          ['left', 'right'].map(async (side) => {
            if (oppositeItem[side]?._key.indexOf('deep') === -1) {
              const sideToDelete = oppositeItem[side];
              const sideToDeleteId = sideToDelete.getInstanceId();
              const sideToDeleteKey = sideToDelete._key;

              const key = swapSideKey(sideToDeleteKey);
              const { style } = sideToDelete;

              this.deleteItem(sideToDelete);
              const ProperSide = ItemMap.side[key];
              const sideInstance = new ProperSide(this._threekitApi);
              await sideInstance.init();
              const sideConnector = new Connector({
                target: oppositeItem,
                targetLocalSide: side,
                alignment: oppositeItem[getAlignKeyword(side)],
                localSide: 'bottom',
              });
              this.addItem(sideInstance, sideConnector);
              sideInstance.style = style;
              convertSideEvent(
                sideToDeleteId,
                sideToDeleteKey,
                sideInstance.getInstanceId(),
                sideInstance._key
              );
            }
          });
        }
        const connectedTranslation = oppositeItem.translation;

        snapTranslation = {
          x: connectedTranslation.x - initTranslation.x,
          y: 0,
          z: connectedTranslation.z - initTranslation.z,
        };

        if (
          itemToDelete._type === 'anytable' &&
          oppositeItem._type === 'side'
        ) {
          const anytableConnector = new Connector({
            target: oppositeItem,
            targetLocalSide: 'top',
            localSide: thisSide,
          });
          connections.push([target, anytableConnector]);
        } else {
          connections.push([oppositeItem, newConnector]);
        }
      }
    } else if (itemToDelete._type === 'seat') {
      const sideConnection = SIDE_KEYWORD.map((side) => {
        if (itemToDelete[side] && itemToDelete[side]._type === 'side')
          return itemToDelete[side];
        return null;
      }).filter((ite) => !!ite);

      deletedNodes = deletedNodes.concat(sideConnection);

      SIDE_KEYWORD.filter(
        (side) => !!itemToDelete[side] && itemToDelete[side]._type === 'seat'
      ).forEach((side) => {
        for (const set of isolateSets) {
          if (set.has(itemToDelete[side])) {
            return;
          }
        }
        const traverseSet = depthFirstTraverse(
          itemToDelete[side],
          new Set([itemToDelete])
        );

        isolateSets.push(traverseSet);
      });

      isolateSets.forEach((set) => set.delete(itemToDelete));
    }

    const resultIsland = { ...this };

    if (update) {
      try {
        deletedNodes.forEach((deleteItem) => {
          this.popItem(deleteItem);
          const instanceId = deleteItem.getInstanceId();
          this._threekitApi.scene.deleteNode(instanceId);
        });
        connections.forEach((connection) =>
          this._connectRecersive(...connection)
        );
      } catch (e) {
        console.error(e);
        // the item does not initialed
      }
    } else {
      const filter = new Set();
      deletedNodes.forEach((n) => filter.add(n._id));
      const items = [];
      const itemMap = {};
      this._items.forEach((item) => {
        if (!filter.has(item._id)) {
          const copy = { ...item };
          SIDE_KEYWORD.forEach((side) => {
            if (copy[side] && filter.has(copy[side]._id)) {
              copy[side] = null;
            }
          });
          itemMap[copy._id] = copy;
          items.push(copy);
        }
      });
      connections.forEach((connection) => {
        const [oppositeItem, connector] = connection;
        itemMap[oppositeItem._id][connector.localSide] =
          itemMap[connector.target._id];
        itemMap[connector.target._id][connector.targetLocalSide] =
          itemMap[oppositeItem._id];
      });
      items.forEach((item) => {
        SIDE_KEYWORD.forEach((side) => {
          if (item[side] && itemMap[item[side]._id]) {
            item[side] = itemMap[item[side]._id];
          }
        });
      });
      resultIsland._items = items;
    }

    return { isolateSets, snapTranslation, deletedNodes, resultIsland };
  };

  popItem = (item) => {
    const itemIdx = this._items.indexOf(item);
    if (itemIdx === -1) throw new Error('Delete Item Error, item not exist!');

    this._items.splice(itemIdx, 1);
    item._disconnect();
    if (item === this._firstItem) {
      this._setFirstItem();
    }
    if (item === this._lastItem) {
      this._setLastItem();
    }
    return item;
  };

  getSeatItems = () => this._getItemsByType('seat');

  getSideItems = () => this._getItemsByType('side');

  getItems = () => [...this._items];

  getItemByInstanceId = (instanceId) =>
    this._items.find((item) => item.getInstanceId() === instanceId);

  toJson = () => {
    const itemJsonArr = this._items.map((item) => item.toJsonObj());
    return JSON.stringify({
      _items: itemJsonArr,
      _firstItem: this._firstItem && this._firstItem.getInstanceId(),
      _id: this._id,
    });
  };

  fromJson = (json) => {
    const obj = JSON.parse(json);
    // getValueProperties(this).forEach((val) => {
    //   if (!obj.hasOwnProperty(val)) throw new Error('Json format is invalid!');
    // });

    const { _items, _id } = obj;
    const { _threekitApi } = this;
    const _firstItem = obj._firstItem || _items[0]._id;

    if (!_firstItem) return;

    // construct the graph with dfs, the rootId is the instanceId from the incoming json
    const createGraph = (rootItemId) => {
      if (!rootItemId) return null;

      const rootItemData = remainMap.get(rootItemId);
      let rootItem = createMap.get(rootItemId);
      if (!rootItemData) return rootItem; // node has already visited

      if (!rootItem) {
        rootItem = new ItemMap[rootItemData._type][rootItemData._key](
          _threekitApi,
          rootItemData
        );
        createMap.set(rootItemId, rootItem);
      }

      rootItem.fromJsonObj(rootItemData);
      remainMap.delete(rootItemId); // remove the item from map

      SIDE_KEYWORD.filter((side) => rootItemData.hasOwnProperty(side)).forEach(
        (side) => {
          rootItem[side] = createGraph(rootItemData[side]);
        }
      );

      return rootItem;
    };
    createGraph.bind(this);

    const remainMap = new Map(_items.map((item) => [item._id, item]));
    const createMap = new Map();

    const firstItemData = remainMap.get(_firstItem);
    const newFirstItem = new ItemMap[firstItemData._type][firstItemData._key](
      _threekitApi,
      firstItemData
    );

    createMap.set(firstItemData._id, newFirstItem);
    createGraph(firstItemData._id);
    this._firstItem = newFirstItem;
    this._items = [...createMap].map((pair) => pair[1]);
    this._id = _id;
    this._setLastItem();

    return this;
  };

  init = () => Promise.all(this._items.map((item) => item.init()));

  incrementItemRotation = async (item) => {
    if (!this._items.includes(item))
      throw new Error(
        'setItemRotation Error, item does not belongs to the island'
      );
    if (item._type === 'side')
      throw new Error(
        'Side are not allowed to rotate, its depend on the connection with seat'
      );

    const allConnections = item.getConnections();

    const validConnections = allConnections.filter(
      ({ target }) => target._type !== 'side'
    );
    const isAnytable = item._type === 'anytable';

    if (!validConnections.length) {
      if (isAnytable && allConnections.length === 1) {
        // other connections are sides
        item._disconnect();
        item.setRotation(item.rotation + 90);
        allConnections.forEach(
          ({ alignment, localSide, target, targetLocalSide }) => {
            const nextConnector = {
              target,
              targetLocalSide,
              alignment,
              localSide: NEXT_SEAT_SIDES[localSide],
            };
            item._connectTo(nextConnector);
          }
        );
      } else {
        // if no seat connection exist, just rotate the item and all connected sides, this case all connect sides will rotate together with seat
        item.setRotation(item.rotation + 90);
        allConnections.forEach(({ localSide, target, targetAlignment }) => {
          target._connectTo({
            target: item,
            targetLocalSide: localSide,
            alignment: targetAlignment,
          });
        });
      }
    } else {
      // this is the povit item which stays the same after this item rotation. The povit item must be a seat item
      // the logic here to make sure the same povit item will be select for continue rotate the same item
      // is to calculate the all edge rotation of this item in the world space and find the edge that has smallest world rotation that connect to a seat
      const seatConnectWithSideRotation = validConnections
        .map((connection) => ({
          ...connection,
          localSideRotation: item._getLocalSideWorldRotation(
            connection.localSide
          ),
        }))
        .sort((a, b) => a.localSideRotation - b.localSideRotation);

      const povit = seatConnectWithSideRotation[0];

      // disconnect item and reconnect the item to povit with the new local side
      item._disconnect();

      const rotateBack =
        item._type === 'seat' &&
        item._key !== 'wedge' &&
        item.rotation > 0 &&
        item.rotation <= 90;
      let localSide = getNextValidLocalSide(item, povit.localSide);
      if (rotateBack) {
        localSide = OPPOSITE_SEAT_SIDES[localSide];
      }

      item._connectTo({
        target: povit.target,
        targetLocalSide: povit.targetLocalSide,
        alignment: ALIGNMENT_MAP[povit.alignment],
        localSide,
      });

      const sideConvertionRequired =
        !isAnytable &&
        !(
          seatConnectWithSideRotation.length === 1 ||
          (seatConnectWithSideRotation.length === 2 &&
            seatConnectWithSideRotation[0].localSide !==
              OPPOSITE_SEAT_SIDES[seatConnectWithSideRotation[1].localSide])
        );

      const newConnections = allConnections.reduce((acc, connection) => {
        if (connection.target === povit.target) return acc;
        let side = getNextValidLocalSide(item, connection.localSide);
        if (rotateBack) {
          side = OPPOSITE_SEAT_SIDES[side];
        }
        const newConnector = new Connector({
          target: item,
          targetLocalSide: side,
          alignment: connection.alignment,
          localSide: connection.targetLocalSide,
        });
        acc[side] = { connector: connection, newConnector };
        return acc;
      }, {});

      await Promise.all(
        Object.values(newConnections).map(
          async ({
            connector: { target, targetLocalSide, alignment, localSide },
            newConnector,
          }) => {
            const side = newConnector.targetLocalSide;

            if (
              target._type === 'seat' ||
              !sideConvertionRequired ||
              target.width === item.getLocalSideDimension(side)
            ) {
              if (target._type === 'side' && item._type === 'seat') {
                if (/left|right/.test(side)) {
                  const prevSide = PREVIOUS_SEAT_SIDES[side];
                  const nextSide = NEXT_SEAT_SIDES[side];
                  if (
                    newConnections[prevSide] &&
                    newConnections[prevSide].connector.target._type === 'side'
                  ) {
                    newConnector.alignment = 'bottom';
                  } else if (
                    newConnections[nextSide] &&
                    newConnections[nextSide].connector.target._type === 'side'
                  ) {
                    newConnector.alignment = 'top';
                  }
                }
              }
              this._connectRecersive(target, newConnector);
            } else {
              const deleteItemId = target.getInstanceId();
              const deleteItemKey = target._key;

              const key = swapSideKey(target._key);

              const { style } = target;

              this.deleteItem(target);
              const ProperSide = ItemMap.side[key];
              const sideInstance = new ProperSide(this._threekitApi);
              this.addItem(sideInstance, newConnector);
              await sideInstance.init();
              sideInstance.style = style;
              convertSideEvent(
                deleteItemId,
                deleteItemKey,
                sideInstance.getInstanceId(),
                sideInstance._key
              );
            }
          }
        )
      );
    }
  };

  intersectWithIsland = (targetIsland) => {
    for (const thisItem of this._items) {
      for (const targetItem of targetIsland.getItems()) {
        if (thisItem.intersectWith(targetItem)) {
          return true;
        }
      }
    }

    return false;
  };

  applyTranslation = (translation) => {
    this._items.forEach((item) => {
      const newTrans = { ...item.translation };
      newTrans.x += translation.x || 0;
      newTrans.z += translation.z || 0;
      item.setTranslation(newTrans);
    });
  };

  getBoundingBox = () =>
    this._items.reduce(
      (box, item) => box.union(item.getBoundingBox()),
      new this._threekitApi.THREE.Box3()
    );

  updatePosition = () => {
    const { min, max } = this.getBoundingBox();
    const position = {
      x: (min.x + max.x) / 2,
      y: (min.y + max.y) / 2,
      z: (min.z + max.z) / 2,
    };

    this.position = position;

    return { position };
  };
}
